import React, { useCallback, useEffect, useRef, useState } from "react";
import { useEditMode } from "@/contexts/CmsContext";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ensureTextLoaded, subscribeText, getCachedText, stageText,
} from "@/lib/slotCache";

// <EditableText slot="home.hero.title" defaults={{en, fr, es}} as="h1" multiline />
//
// Behaviour:
//   • OFF mode  → renders the literal text in the requested tag (zero overhead).
//   • ON mode   → same tag, but contentEditable with a soft amber ring.
//                 Save on blur (with a diff check so we never PUT empty/equal).
//                 ESC reverts. Enter blurs (unless `multiline`).
//                 Paste is forced to plain text.
//                 Saves ONLY the currently active language; the other two are preserved.
export const EditableText = ({
  slot,
  defaults = {},
  as: Tag = "span",
  multiline = false,
  className = "",
  children,
  ...rest
}) => {
  const { textEditMode } = useEditMode();
  const { lang } = useLanguage();
  const [stored, setStored] = useState(() => getCachedText(slot));
  const elRef = useRef(null);

  useEffect(() => {
    ensureTextLoaded();
    const unsubscribe = subscribeText(slot, (next) => {
      setStored(next ? { ...next } : next);
    });
    return unsubscribe;
  }, [slot]);

  const currentValue =
    (stored && stored[lang]) ||
    defaults[lang] ||
    defaults.en ||
    defaults.fr ||
    defaults.es ||
    "";

  // Sync the DOM when the value changes from outside (e.g. another tab edits)
  useEffect(() => {
    if (!textEditMode) return;
    const el = elRef.current;
    if (!el) return;
    if (document.activeElement === el) return; // don't yank text away mid-edit
    if (el.innerText !== currentValue) el.innerText = currentValue;
  }, [currentValue, textEditMode]);

  const handleBlur = useCallback(async () => {
    const el = elRef.current;
    if (!el) return;
    const newText = el.innerText.replace(/\u00A0/g, " ").trim();
    const previous = (stored && stored[lang]) ?? defaults[lang] ?? "";
    if (newText === previous) return; // diff: skip no-op stages
    try {
      stageText(slot, { [lang]: newText });
    } catch (err) {
      console.error("[CMS] stage text failed", err);
    }
  }, [slot, stored, lang, defaults]);

  const handleKeyDown = useCallback((e) => {
    if (!multiline && e.key === "Enter") {
      e.preventDefault();
      elRef.current?.blur();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      if (elRef.current) elRef.current.innerText = currentValue;
      elRef.current?.blur();
    }
  }, [multiline, currentValue]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData?.getData("text/plain") || "";
    document.execCommand("insertText", false, text);
  }, []);

  // OFF mode → boring fast render
  if (!textEditMode) {
    return <Tag className={className} data-cms-text-slot={slot} {...rest}>{children ?? currentValue}</Tag>;
  }

  // ON mode → contentEditable with editorial soft ring
  return (
    <Tag
      ref={elRef}
      className={`${className} editable-text-on`}
      contentEditable
      suppressContentEditableWarning
      data-cms-text-slot={slot}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onMouseDownCapture={(e) => e.stopPropagation()}
      onClickCapture={(e) => e.stopPropagation()}
      data-testid={`editable-text-${slot}`}
      {...rest}
    >
      {currentValue}
    </Tag>
  );
};

export default EditableText;
